import { CSSProperties, ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { isTopmostOverlay, lockBodyScroll } from './overlayStack'
import { useOverlayFocusTrap } from './useOverlayFocusTrap'

export interface DrawerProps {
    open: boolean
    onClose: () => void
    ariaLabelledBy?: string
    position?: 'bottom' | 'top' | 'right' | 'left'
    heading?: ReactNode
    footer?: ReactNode
    showCloseButton?: boolean
    children: ReactNode
    width?: number | string
    /** Rules off the heading and the footer (a bottom and a top border), as a side panel with a sticky action bar has them. */
    divided?: boolean
    style?: CSSProperties
}

const positionClasses: Record<string, string> = {
    bottom: 'inset-x-0 bottom-0 w-full rounded-t-2xl',
    top: 'inset-x-0 top-0 w-full rounded-b-2xl',
    right: 'inset-y-0 right-0 h-full rounded-l-2xl',
    left: 'inset-y-0 left-0 h-full rounded-r-2xl',
}

export const Drawer = ({
    open,
    onClose,
    ariaLabelledBy,
    position = 'bottom',
    heading,
    footer,
    showCloseButton = true,
    children,
    width = 393,
    divided = false,
    style,
}: DrawerProps) => {
    const panelRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    useOverlayFocusTrap(panelRef, open)

    useEffect(() => {
        if (!open) return
        const handler = (e: KeyboardEvent) => {
            // A Modal opened from this drawer owns the keyboard until it closes.
            if (e.key === 'Escape' && isTopmostOverlay(panelRef.current)) onCloseRef.current()
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [open])

    useEffect(() => {
        if (!open) return
        const unlockScroll = lockBodyScroll()
        return () => {
            unlockScroll()
        }
    }, [open])

    if (!open) return null

    const isVertical = position === 'bottom' || position === 'top'

    const drawer = (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[9998] h-[100vh] bg-black/50 backdrop-blur-[12px]" onClick={onClose} />

            {/* Panel — z-[9998], level with the backdrop (DOM order still puts it on top of its own backdrop).
                At z-[9999] it sat above the backdrop of a Modal opened later, so the drawer stayed live behind it. */}
            <div
                ref={panelRef}
                data-ds2-overlay
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledBy}
                className={`fixed z-[9998] flex flex-col overflow-hidden bg-surface shadow-overlay ${positionClasses[position]}`}
                // A side drawer never runs past the viewport: on a narrow screen it takes the full width.
                style={isVertical ? style : { width, maxWidth: '100%', ...style }}
            >
                {/* Close button */}
                {showCloseButton && (
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-4 top-4 z-10 flex cursor-pointer items-center rounded-xl border-none bg-default p-1 text-default-foreground"
                    >
                        <X size={16} strokeWidth={1.5} />
                    </button>
                )}

                {/* Heading */}
                {heading && <div className={`pl-6 pt-6 ${showCloseButton ? 'pr-12' : 'pr-6'} ${divided ? 'border-b border-border pb-5' : ''}`}>{heading}</div>}

                {/* Body */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">{children}</div>

                {/* Footer */}
                {footer && <div className={`flex justify-end gap-2 px-6 ${divided ? 'border-t border-border bg-surface py-4' : 'pb-6'}`}>{footer}</div>}
            </div>
        </>
    )

    // A fixed element inside the app's scroll container is fixed to that container, not the
    // viewport. Rendering at the body level makes the backdrop cover the complete viewport.
    return createPortal(drawer, document.body)
}
